import React from 'react';

import { Wrapper } from './styles.ts';

export type ErrorFormatter = (message: string) => string;

interface ErrorRendererArgs {
  errorVisible: boolean;
  error?: string;
}

export type ErrorRenderer = (args: ErrorRendererArgs) => React.ReactNode;

export interface ErrorProps<TName extends string> {
  /**
   * Name of the related field.
   */
  name: TName;
  /**
   * Optional formatter for the error message.
   */
  formatMessage?: ErrorFormatter;
  /**
   * If `true` the error will only be displayed if the field has been touched by the user.
   * @default true
   */
  withTouched?: boolean;
  /**
   * `True` indicated that field has been touched by the user.
   */
  touched?: boolean;
  /**
   * Field level error message.
   */
  error?: string;
  /**
   * Form level error message.
   */
  submitError?: string;
  renderError?: ErrorRenderer;
}

const defaultFormatMessage: ErrorFormatter = (message) => message;

export const isErrorVisible = (error: string | undefined, touched = false, withTouched = true, submitError?: string): boolean => {
  const visible = withTouched ? touched : true;

  return Boolean((visible && error) || submitError);
};

const Error = <TName extends string>({
  name,
  formatMessage = defaultFormatMessage,
  withTouched = true,
  touched,
  submitError,
  error,
  renderError,
}: ErrorProps<TName>) => {
  const errorVisible = isErrorVisible(error, touched, withTouched, submitError);
  const currentError = error || submitError;
  const formattedError = typeof currentError === 'string' ? formatMessage(currentError) : undefined;

  if (!errorVisible && !renderError) {
    return null;
  }
  return (
    <div id={`${name}-error`} data-testid={`${name}-error`}>
      <Wrapper>{renderError ? renderError({ errorVisible, error: formattedError }) : errorVisible && formattedError}</Wrapper>
    </div>
  );
};

export default React.memo(Error) as typeof Error;
